import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  Rating,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '@services/authService';

interface Clinic {
  id: string;
  name: string;
  address: any; // JSONB - poate fi string sau obiect
  phone?: string;
  email?: string;
  logo?: string;
  rating: number;
  reviewsCount: number;
  services: string[];
  specialties: string[];
  isFavorite?: boolean;
  distance?: number; // km
  latitude?: number;
  longitude?: number;
  // Helper properties
  addressText?: string;
  city?: string;
  county?: string;
}

type SortOption = 'favorites' | 'rating-desc' | 'rating-asc' | 'distance' | 'name-asc' | 'name-desc';

const ClinicsPartner: React.FC = () => {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [filteredClinics, setFilteredClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('favorites');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Solicita locatia utilizatorului
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (_error) => {
          // Location access denied
        }
      );
    }
  }, []);

  // Incarca clinicile
  useEffect(() => {
    loadClinics();
  }, []);

  const loadClinics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/clinics/partner');
      let clinicsData = response.data.data || [];

      // Proceseaza datele pentru a extrage informatii din JSONB
      clinicsData = clinicsData.map((clinic: any) => {
        // Extrage address text
        let addressText = '';
        let city = '';
        let county = '';
        
        if (typeof clinic.address === 'string') {
          addressText = clinic.address;
        } else if (clinic.address && typeof clinic.address === 'object') {
          addressText = clinic.address.street || clinic.address.full || '';
          city = clinic.address.city || clinic.address.locality || '';
          county = clinic.address.county || '';
        }

        // Calculeaza distanta daca avem locatia utilizatorului
        let distance = undefined;
        if (userLocation && clinic.latitude && clinic.longitude) {
          distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            clinic.latitude,
            clinic.longitude
          );
        }

        return {
          ...clinic,
          addressText,
          city: city || 'Necunoscut',
          county,
          distance,
          logo: clinic.logo || clinic.logo_url
        };
      });

      setClinics(clinicsData);
      setFilteredClinics(clinicsData);
    } catch (err: any) {
      console.error('Error loading clinics:', err);
      setError('Eroare la incarcarea clinicilor partenere');
    } finally {
      setLoading(false);
    }
  };


  // Calculeaza distanta intre doua puncte (formula Haversine)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Raza Pamantului in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Aplica filtre si sortare
  useEffect(() => {
    let result = [...clinics];

    // Filtrare dupa cautare
    if (searchTerm) {
      result = result.filter(clinic =>
        clinic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.addressText?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrare dupa oras
    if (cityFilter !== 'all') {
      result = result.filter(clinic => clinic.city === cityFilter);
    }

    // Sortare
    result.sort((a, b) => {
      switch (sortBy) {
        case 'favorites':
          // Favorite first, then by rating
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          return b.rating - a.rating;
        case 'rating-desc':
          return b.rating - a.rating;
        case 'rating-asc':
          return a.rating - b.rating;
        case 'distance':
          if (!a.distance) return 1;
          if (!b.distance) return -1;
          return a.distance - b.distance;
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    setFilteredClinics(result);
  }, [clinics, searchTerm, sortBy, cityFilter]);

  const toggleFavorite = async (clinicId: string) => {
    try {
      await api.post(`/clinics/${clinicId}/favorite`);
      
      // Update local state
      setClinics(prev => prev.map(clinic =>
        clinic.id === clinicId
          ? { ...clinic, isFavorite: !clinic.isFavorite }
          : clinic
      ));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleAppointment = (clinicId: string) => {
    navigate(`/client/clinics/${clinicId}/appointments/new`);
  };

  // Get unique cities for filter
  const cities = Array.from(new Set(clinics.map(c => c.city))).sort();

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Se incarca clinicile partenere...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          🏥 Clinici Partenere
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gaseste cea mai potrivita clinica veterinara pentru animalul tau
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filtre si Sortare */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Cautare */}
        <TextField
          placeholder="Cauta clinica, oras..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: '1 1 300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Filtrare oras */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Oras</InputLabel>
          <Select
            value={cityFilter}
            label="Oras"
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <MenuItem value="all">Toate</MenuItem>
            {cities.map(city => (
              <MenuItem key={city} value={city}>{city}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sortare */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sorteaza dupa</InputLabel>
          <Select
            value={sortBy}
            label="Sorteaza dupa"
            onChange={(e) => setSortBy(e.target.value as SortOption)}
          >
            <MenuItem value="favorites">⭐ Clinici favorite</MenuItem>
            <MenuItem value="rating-desc">🔝 Rating (descrescator)</MenuItem>
            <MenuItem value="rating-asc">🔝 Rating (crescator)</MenuItem>
            {userLocation && <MenuItem value="distance">📍 Distanta</MenuItem>}
            <MenuItem value="name-asc">🔤 A → Z</MenuItem>
            <MenuItem value="name-desc">🔤 Z → A</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Rezultate */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredClinics.length} {filteredClinics.length === 1 ? 'clinica gasita' : 'clinici gasite'}
      </Typography>

      {/* Grid de clinici */}
      <Grid container spacing={3}>
        {filteredClinics.map((clinic) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={clinic.id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}>
              {/* Cover Photo */}
              <Box sx={{ 
                height: 120, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Favorite Button */}
                <IconButton
                  onClick={() => toggleFavorite(clinic.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                  }}
                  size="small"
                >
                  {clinic.isFavorite ? (
                    <FavoriteIcon color="error" />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </IconButton>

                {/* Logo suprapus in coltul stanga jos */}
                <Avatar
                  src={clinic.logo}
                  sx={{ 
                    width: 64, 
                    height: 64, 
                    position: 'absolute',
                    bottom: -32,
                    left: 16,
                    border: '4px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    bgcolor: 'primary.main'
                  }}
                >
                  <BusinessIcon sx={{ fontSize: '1.5rem' }} />
                </Avatar>
              </Box>

              <CardContent sx={{ flexGrow: 1, pb: 1, pt: 4 }}>
                {/* Nume clinica */}
                <Typography variant="h6" sx={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 600, 
                  mb: 1,
                  color: 'text.primary'
                }}>
                  {clinic.name}
                </Typography>

                {/* Adresa cu icon */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {clinic.addressText || 'Adresa nedisponibila'}{clinic.city && `, ${clinic.city}`}
                  </Typography>
                </Box>

                {/* Rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Rating value={clinic.rating} precision={0.1} size="small" readOnly />
                  <Typography variant="caption" color="text.secondary">
                    ⭐ {clinic.rating.toFixed(1)} ({clinic.reviewsCount} recenzii)
                  </Typography>
                </Box>

                {/* Distanta */}
                {clinic.distance && (
                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1.5 }}>
                    📍 {clinic.distance.toFixed(1)} km distanta
                  </Typography>
                )}


                {/* Servicii principale */}
                {clinic.services && clinic.services.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                    {clinic.services.slice(0, 3).map((service, idx) => (
                      <Chip
                        key={idx}
                        label={service}
                        size="small"
                        sx={{ 
                          fontSize: '0.75rem', 
                          height: 24,
                          bgcolor: 'primary.light',
                          color: 'white',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    ))}
                    {clinic.services.length > 3 && (
                      <Chip
                        label={`+${clinic.services.length - 3}`}
                        size="small"
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ScheduleIcon />}
                  onClick={() => handleAppointment(clinic.id)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none', 
                    fontWeight: 600,
                    py: 1.2,
                    fontSize: '0.9rem'
                  }}
                >
                  Programeaza vizita
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/client/clinics/${clinic.id}`)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  Vezi mai multe
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredClinics.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nu am gasit clinici
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Incearca sa modifici criteriile de cautare sau filtrele
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ClinicsPartner;

